import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useLogin } from '../hooks/useAuth';
import { useAuthStore } from '../store/authStore';
import { Button, Input, Card } from '../components';
import { Mail, Lock } from 'lucide-react';

type LoginFormData = {
  email: string;
  password: string;
};

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const { t } = useTranslation();

  const loginSchema = z.object({
    email: z.string().email(t('validation.invalidEmail')),
    password: z.string().min(6, t('validation.passwordMin6')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await loginMutation.mutateAsync(data);
      const user = useAuthStore.getState().user;
      if (user && user.role === 'admin') {
        navigate('/admin');
      } else if (user && user.role === 'opleider') {
        navigate('/opleider/dashboard');
      } else if (user && user.role === 'medewerker') {
        navigate('/medewerker');
      } else if (user && (user.role === 'opdrachtgever' || user.role === 'employer_member')) {
        navigate('/employer/dashboard');
      } else {
        navigate('/professional/dashboard');
      }
    } catch {
      // error handling is done in the hook
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img
            src="/belagom-text.png"
            alt="BeLagom"
            className="h-10 mx-auto mb-4 cursor-pointer"
            onClick={() => navigate('/')}
          />
          <h2 className="text-3xl font-bold text-gray-900">{t('login.title')}</h2>
          <p className="mt-2 text-gray-600">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="text-blue-600 hover:text-blue-500 font-medium">
              {t('login.registerHere')}
            </Link>
          </p>
        </div>

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              label={t('login.email')}
              type="email"
              icon={<Mail />}
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label={t('login.password')}
              type="password"
              icon={<Lock />}
              error={errors.password?.message}
              {...register('password')}
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  {t('login.rememberMe')}
                </label>
              </div>

              <div className="text-sm">
                <Link to="/forgot-password" className="text-blue-600 hover:text-blue-500">
                  {t('login.forgotPassword')}
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={loginMutation.isPending}
            >
              {t('login.submit')}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('login.testAccounts')}</span>
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-600 space-y-1">
              <p><strong>{t('login.demoInfo')}</strong></p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
